import { useEffect, useState } from "react";
import { AlertTriangle, Building2, Check, ChevronLeft, ChevronRight, ExternalLink, Mail, Plus, Send, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const empty = { receiver_name: "", receiver_rfc: "", receiver_email: "", description: "", quantity: "1", unit_price: "" };

const GATE_LABELS = {
  profile_complete: "Datos fiscales (RFC, razón social, régimen, CP)",
  csd_valid: "Certificado de Sello Digital (CSD)",
  efirma_valid: "e.firma cifrada",
  manifest_signed: "Carta Manifiesto firmada",
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [issuer, setIssuer] = useState(null);
  const [defaults, setDefaults] = useState({ iva_rate: 16, currency: "MXN" });
  const [validation, setValidation] = useState({ ready: true, missing: [], gates: {}, block_reason: null, block_message: "" });

  const refreshValidation = () => {
    return api.get("/business-profile/invoice-defaults", { withCredentials: true })
      .then((r) => {
        setIssuer(r.data.issuer);
        setDefaults(r.data.defaults || { iva_rate: 16, currency: "MXN" });
        setValidation({
          ready: r.data.ready,
          missing: r.data.missing || [],
          gates: r.data.gates || {},
          block_reason: r.data.block_reason,
          block_message: r.data.block_message || "",
        });
      })
      .catch(() => setIssuer(null));
  };
  useEffect(() => { refreshValidation(); }, []);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const ivaRate = Number(defaults.iva_rate ?? 16) / 100;
  const total = Number(form.quantity || 0) * Number(form.unit_price || 0) * (1 + ivaRate);

  async function create() {
    setError("");
    if (!validation.ready) {
      setError(validation.block_message || "No puedes emitir facturas todavía. Completa tu Perfil Fiscal y firma tu Carta Manifiesto para habilitar la facturación.");
      return;
    }
    try {
      const r = await api.post("/invoices", {
        receiver_name: form.receiver_name,
        receiver_rfc: form.receiver_rfc,
        receiver_email: form.receiver_email,
        usage_code: "G03",
        items: [{ description: form.description, quantity: Number(form.quantity), unit_price: Number(form.unit_price) }],
      });
      setInvoice(r.data);
      setStep(3);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        setError(detail.message);
        refreshValidation();
      } else {
        setError(typeof detail === "string" ? detail : "Revisa los datos y vuelve a intentarlo.");
      }
    }
  }

  const currencyFmt = new Intl.NumberFormat("es-MX", { style: "currency", currency: defaults.currency || "MXN", minimumFractionDigits: 2 });

  return (
    <AppShell>
      <div className="page invoice-page">
        <section className="simple-intro">
          <small className="eyebrow">FACTURAR SIN PALABRAS RARAS</small>
          <h1 data-testid="invoices-heading">Hagamos tu<br /><em>factura.</em></h1>
          <p data-testid="invoices-intro-text">Te acompaño en tres pasitos. Tú solo cuéntame lo básico.</p>
        </section>

        {/* Fiscal gate banner — blocks CFDI emission until profile + CSD + e.firma + Carta Manifiesto are all valid */}
        {!validation.ready && (
          <section className="fiscal-gate-banner" data-testid="fiscal-gate-banner" style={{
            padding: "16px 18px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 14,
            marginBottom: 16, display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
          }}>
            <AlertTriangle size={22} color="#dc2626" style={{ flex: "0 0 auto", marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <b style={{ display: "block", font: '800 14px "Nunito", sans-serif', color: "#7f1d1d', marginBottom: 4" }}>
                No puedes emitir facturas todavía
              </b>
              <p style={{ margin: "4px 0 8px", color: "#991b1b", fontSize: 13, lineHeight: 1.4 }}>
                {validation.block_message || "Completa tu Perfil Fiscal y firma tu Carta Manifiesto para habilitar la facturación."}
              </p>
              <ul data-testid="fiscal-gate-checklist" style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gap: 4 }}>
                {Object.entries(GATE_LABELS).map(([key, label]) => {
                  const ok = validation.gates?.[key];
                  return (
                    <li key={key} data-testid={`fiscal-gate-${key}`} style={{ fontSize: 12, color: ok ? "#065f46" : "#7f1d1d", fontWeight: 700 }}>
                      {ok ? "✓" : "○"} {label}
                    </li>
                  );
                })}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => navigate("/perfil-negocio?tab=fiscal")}
              className="cash-btn primary"
              data-testid="fiscal-gate-complete-cta"
              style={{ flex: "0 0 auto", background: "#dc2626", whiteSpace: "nowrap" }}
            >
              Completar Perfil Fiscal <ChevronRight size={14} />
            </button>
          </section>
        )}

        {/* Emisor auto-populated banner from Perfil de Negocio */}
        {issuer && (
          <div
            className="invoice-issuer-banner"
            data-testid="invoice-issuer-banner"
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px", background: validation.ready ? "#eef2ff" : "#fef3c7",
              border: `1px solid ${validation.ready ? "#c7d2fe" : "#fde68a"}`,
              borderRadius: 14, marginBottom: 16, flexWrap: "wrap",
            }}
          >
            {validation.ready ? <Building2 size={20} color="#4169e1" /> : <AlertTriangle size={20} color="#f59e0b" />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ font: '800 13px "Nunito", sans-serif', color: "#0f172a", display: "block" }}>
                {validation.ready
                  ? `Emitiendo como: ${issuer.business_name || issuer.legal_name || "Tu empresa"}`
                  : "Faltan campos oficiales del SAT en tu Perfil de Negocio"}
              </b>
              {validation.ready
                ? <small style={{ color: "#475569" }}>RFC {issuer.rfc || "—"} · {issuer.tax_regime || "Sin régimen"} · CP {issuer.cfdi_zip || "—"}</small>
                : <small style={{ color: "#78350f" }}>Necesitas: {validation.missing.map((m) => m.label).join(", ")}</small>
              }
            </div>
            <Link to="/perfil-negocio" className="cash-btn ghost" data-testid="invoice-edit-issuer-link" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <ExternalLink size={13} /> {validation.ready ? "Editar emisor" : "Completar ahora"}
            </Link>
          </div>
        )}

        <div className="steps" data-testid="invoice-stepper">
          {["Tu cliente", "Lo que vendiste", "¡Listo!"].map((name, i) => (
            <span className={step >= i + 1 ? "on" : ""} key={name} data-testid={`invoice-step-${i + 1}`}>
              <b>{step > i + 1 ? <Check size={14} /> : i + 1}</b>{name}
            </span>
          ))}
        </div>

        <section className="wizard" data-testid="invoice-wizard">
          {step === 1 && <>
            <h2>¿A quién le facturamos?</h2>
            <p>Es la persona o empresa que recibirá tu factura.</p>
            <label>Nombre o razón social<input name="receiver_name" value={form.receiver_name} onChange={update} placeholder="Ej. Café La Esquina" data-testid="invoice-receiver-name-input" /></label>
            <label>RFC<input name="receiver_rfc" value={form.receiver_rfc} onChange={update} placeholder="12 o 13 caracteres" data-testid="invoice-receiver-rfc-input" /></label>
            <label>Correo electrónico<input name="receiver_email" value={form.receiver_email} onChange={update} placeholder="hola@cliente.com" data-testid="invoice-receiver-email-input" /></label>
            <button className="primary next" disabled={!form.receiver_name || form.receiver_rfc.length < 12 || !form.receiver_email} onClick={() => setStep(2)} data-testid="invoice-step-one-next-button">Siguiente <ChevronRight size={18} /></button>
          </>}

          {step === 2 && <>
            <h2>¿Qué vendiste?</h2>
            <p>Una descripción sencilla es suficiente.</p>
            <label>Descripción<input name="description" value={form.description} onChange={update} placeholder="Ej. Diseño de logotipo" data-testid="invoice-description-input" /></label>
            <div className="two">
              <label>Cantidad<input name="quantity" type="number" min="1" value={form.quantity} onChange={update} data-testid="invoice-quantity-input" /></label>
              <label>Precio sin IVA<input name="unit_price" type="number" min="1" value={form.unit_price} onChange={update} placeholder="$0" data-testid="invoice-price-input" /></label>
            </div>
            <div className="total" data-testid="invoice-total-preview">
              <b>Total aproximado (incluye IVA {(ivaRate * 100).toFixed(0)}%)</b>
              <strong>{currencyFmt.format(total)}</strong>
            </div>
            {error && <small className="error" data-testid="invoice-form-error">{error}</small>}
            <div className="actions">
              <button className="plain" onClick={() => setStep(1)} data-testid="invoice-back-button"><ChevronLeft size={18} />Atrás</button>
              <button className="primary" disabled={!form.description || !form.unit_price || !validation.ready} onClick={create} data-testid="invoice-create-button" title={validation.ready ? "" : "Completa tu Perfil Fiscal para emitir facturas"}>Crear factura <Send size={17} /></button>
            </div>
          </>}

          {step === 3 && invoice && <div className="success" data-testid="invoice-success-state">
            <span><Check /></span>
            <h2>¡Tu factura está lista!</h2>
            <p data-testid="invoice-success-summary">{invoice.folio} · Total {currencyFmt.format(invoice.total)}</p>
            <a className="primary" href={invoice.email_url} data-testid="invoice-email-client-link"><Mail size={17} />Abrir correo y enviarla</a>
            <small data-testid="invoice-stamping-note"><ShieldCheck size={15} />La factura se prepara como borrador profesional. El timbrado PAC se activa al conectar las credenciales fiscales.</small>
            <button className="plain" onClick={() => { setForm(empty); setInvoice(null); setStep(1); }} data-testid="new-invoice-button"><Plus size={17} />Hacer otra factura</button>
          </div>}
        </section>
      </div>
    </AppShell>
  );
}
